import { Storybook } from "@tento-ui/ui/storybook";
import { DshTurnBar } from "./bar";

/** Inline, NOT imported from `icons/*.svg`. The storybook harness bundles a
 * card with bun, which does not hand back `.svg` imports as markup — the
 * string arrives as a path, `LonaIcons.$svg` finds no element in it, and the
 * whole card dies on `null.slot`. The app imports the real icons; an example
 * carries its own so the card can never be broken by the asset pipeline. */
const STROKE =
  'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"';
const RETRY_ICON =
  `<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><g ${STROKE}><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9"/><path d="M13.5 2v3.2h-3.2"/></g></svg>`;
const LINK_ICON =
  `<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><g ${STROKE}><path d="M6.6 9.4a2.6 2.6 0 0 0 3.7 0l2.1-2.1a2.6 2.6 0 0 0-3.7-3.7l-.9.9"/><path d="M9.4 6.6a2.6 2.6 0 0 0-3.7 0L3.6 8.7a2.6 2.6 0 0 0 3.7 3.7l.9-.9"/></g></svg>`;

/** The fixture's own turn, verbatim from `thread/fixture.ts`. */
const FIXTURE_FACES: DshTurnBar.Face[] = [
  { id: "1", tone: "done", label: "Search seq" },
  { id: "2", tone: "done", label: "Read dispatch.rs" },
  { id: "3", tone: "done", label: "Ran cargo test -q -p tento-pane-runtime" },
  { id: "4", tone: "done", label: "Update stream-items.ts" },
  { id: "5", tone: "failed", label: "Ran deno run -A build.deno.ts" },
  { id: "6", tone: "declined", label: "Ran rm -rf ~/.tento-sh/sessions" },
];

export class DshTurnBarStoryBook extends Storybook.Default {
  title(): string {
    return "Turn · dsh · bar";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "The fixture's turn — actions, then metrics, then the run",
        render: () =>
          DshTurnBar.makeWith({
            actions: [
              { id: "copy", label: "quote this turn", icon: LINK_ICON },
              { id: "retry", label: "re-run this turn", icon: RETRY_ICON },
            ],
            meta: [
              { key: "took", value: "1m 51s" },
              { key: "output", value: "3.6k tokens" },
            ],
            faces: FIXTURE_FACES,
          }),
      },
      {
        title: "Metrics only — invisible until the row is hovered",
        render: () =>
          DshTurnBar.makeWith({
            meta: [
              { key: "lane", value: "opencode" },
              { key: "unit", value: "u-104" },
              { key: "rows", value: "12" },
              { key: "at", value: "10:02:14" },
            ],
          }),
      },
      {
        title: "Actions only — nothing to report about the turn yet",
        render: () =>
          DshTurnBar.makeWith({
            actions: [
              { id: "retry", label: "re-run this turn", icon: RETRY_ICON },
            ],
          }),
      },
      {
        title: "Past the cap the remainder becomes a number",
        render: () =>
          DshTurnBar.makeWith({
            meta: [{ key: "took", value: "4m 02s" }],
            faces: [
              ...Array.from({ length: 14 }, (_, index): DshTurnBar.Face => ({
                id: `r${index}`,
                tone: "done",
                label: `Read file-${index}.ts`,
              })),
              { id: "x", tone: "failed", label: "Ran cargo build" },
            ],
          }),
      },
      {
        title: "Under a turn — the bar is the quietest thing on the page",
        render: () => {
          const $wrap = document.createElement("div");
          $wrap.style.width = "100%";
          const $prose = document.createElement("p");
          $prose.style.margin = "0 0 4px";
          $prose.style.fontSize = "14px";
          $prose.style.lineHeight = "1.6";
          $prose.textContent =
            "Both files are byte-identical to how they started, so your current prompt and render policy are untouched.";
          $wrap.append(
            $prose,
            DshTurnBar.makeWith({
              actions: [
                { id: "retry", label: "re-run this turn", icon: RETRY_ICON },
              ],
              meta: [
                { key: "took", value: "9s" },
                { key: "rows", value: "9" },
              ],
              faces: FIXTURE_FACES.slice(0, 3),
            }),
          );
          return $wrap;
        },
      },
    ];
  }
}
